import type { TableSchema, TableSchemaCategory } from './tableSchema.types';
import { loadCustomSchemas } from './customTableSchemas';

export const BLANK_TABLE_SCHEMA: TableSchema = {
  id: 'blank',
  name: 'Blank Table',
  category: 'custom',
  grid: {
    rows: 3,
    cols: 3,
    rowsExpandable: true,
    colsExpandable: true,
    minRows: 1,
    minCols: 1,
  },
  rows: [{ index: 0, isHeader: true, height: 32 }],
  theme: {
    headerBackground: '#F1F5F9',
    borderColor: '#97a3b6',
    headerTextColor: '#0F172A',
  },
  mergedRegions: [],
};

export const SWOT_TABLE_SCHEMA: TableSchema = {
  id: 'swot',
  name: 'SWOT Analysis',
  category: 'strategy',
  grid: {
    rows: 2,
    cols: 2,
    rowsExpandable: false,
    colsExpandable: false,
    minRows: 2,
    minCols: 2,
    maxRows: 2,
    maxCols: 2,
  },
  dimensions: {
    defaultRowHeight: 200,
    defaultColWidth: 250,
    minCellHeight: 100,
    minCellWidth: 150,
  },
  cells: [
    {
      row: 0,
      col: 0,
      text: 'Strengths',
      style: { textAlign: 'left', verticalAlign: 'top', fontWeight: 'bold', padding: 12, marginTop: 32 },
    },
    {
      row: 0,
      col: 1,
      text: 'Weaknesses',
      style: { textAlign: 'left', verticalAlign: 'top', fontWeight: 'bold', padding: 12, marginTop: 32 },
    },
    {
      row: 1,
      col: 0,
      text: 'Opportunities',
      style: { textAlign: 'left', verticalAlign: 'top', fontWeight: 'bold', padding: 12, marginTop: 32 },
    },
    {
      row: 1,
      col: 1,
      text: 'Threats',
      style: { textAlign: 'left', verticalAlign: 'top', fontWeight: 'bold', padding: 12, marginTop: 32 },
    },
  ],
  contentRules: {
    allowedContent: ['text', 'sticky'],
    defaultContent: 'sticky',
  },
};

export const BUSINESS_MODEL_CANVAS_SCHEMA: TableSchema = {
  id: 'business-model-canvas',
  name: 'Business Model Canvas',
  category: 'strategy',
  grid: {
    rows: 3,
    cols: 3,
    rowsExpandable: false,
    colsExpandable: false,
    minRows: 3,
    minCols: 3,
  },
  dimensions: {
    defaultRowHeight: 180,
    defaultColWidth: 220,
    minCellHeight: 100,
    minCellWidth: 140,
  },
  cells: [
    { row: 0, col: 0, text: 'Key Partners' },
    { row: 0, col: 1, text: 'Key Activities' },
    { row: 0, col: 2, text: 'Key Resources' },
    { row: 1, col: 0, text: 'Value Propositions' },
    { row: 1, col: 1, text: 'Customer Relationships' },
    { row: 1, col: 2, text: 'Channels' },
    { row: 2, col: 0, text: 'Customer Segments' },
    { row: 2, col: 1, text: 'Cost Structure' },
    { row: 2, col: 2, text: 'Revenue Streams' },
  ],
  contentRules: {
    allowedContent: ['text', 'sticky', 'card', 'shape'],
    defaultContent: 'sticky',
  },
};

export const LEAN_CANVAS_SCHEMA: TableSchema = {
  id: 'lean-canvas',
  name: 'Lean Canvas',
  category: 'strategy',
  grid: {
    rows: 3,
    cols: 3,
    rowsExpandable: false,
    colsExpandable: false,
    minRows: 3,
    minCols: 3,
  },
  dimensions: {
    defaultRowHeight: 180,
    defaultColWidth: 220,
    minCellHeight: 100,
    minCellWidth: 140,
  },
  cells: [
    { row: 0, col: 0, text: 'Problem' },
    { row: 0, col: 1, text: 'Solution' },
    { row: 0, col: 2, text: 'Key Metrics' },
    { row: 1, col: 0, text: 'Unique Value Proposition' },
    { row: 1, col: 1, text: 'Unfair Advantage' },
    { row: 1, col: 2, text: 'Channels' },
    { row: 2, col: 0, text: 'Customer Segments' },
    { row: 2, col: 1, text: 'Cost Structure' },
    { row: 2, col: 2, text: 'Revenue Streams' },
  ],
  contentRules: {
    allowedContent: ['text', 'sticky', 'card', 'shape'],
    defaultContent: 'sticky',
  },
};

export const KANBAN_TABLE_SCHEMA: TableSchema = {
  id: 'kanban',
  name: 'Kanban Board',
  category: 'agile',
  grid: {
    rows: 1,
    cols: 3,
    rowsExpandable: true,
    colsExpandable: true,
    minRows: 1,
    minCols: 1,
  },
  rows: [{ index: 0, isHeader: true, height: 40 }],
  cells: [
    { row: 0, col: 0, text: 'To Do' },
    { row: 0, col: 1, text: 'In Progress' },
    { row: 0, col: 2, text: 'Done' },
  ],
  dimensions: {
    defaultRowHeight: 160,
    defaultColWidth: 220,
    minCellHeight: 120,
    minCellWidth: 140,
  },
  contentRules: {
    allowedContent: ['card', 'sticky', 'shape'],
    defaultContent: 'card',
  },
};

export const EISENHOWER_MATRIX_SCHEMA: TableSchema = {
  id: 'eisenhower',
  name: 'Eisenhower Matrix',
  category: 'planning',
  grid: {
    rows: 2,
    cols: 2,
    rowsExpandable: false,
    colsExpandable: false,
    minRows: 2,
    minCols: 2,
    maxRows: 2,
    maxCols: 2,
  },
  dimensions: {
    defaultRowHeight: 200,
    defaultColWidth: 250,
    minCellHeight: 120,
    minCellWidth: 150,
  },
  cells: [
    { row: 0, col: 0, text: 'Do First' },
    { row: 0, col: 1, text: 'Schedule' },
    { row: 1, col: 0, text: 'Delegate' },
    { row: 1, col: 1, text: 'Eliminate' },
  ],
  axes: {
    x: { start: 'Not Urgent', end: 'Urgent' },
    y: { start: 'Not Important', end: 'Important' },
  },
  contentRules: {
    allowedContent: ['text', 'sticky', 'card'],
    defaultContent: 'sticky',
  },
};

export const RACI_MATRIX_SCHEMA: TableSchema = {
  id: 'raci',
  name: 'RACI Matrix',
  category: 'planning',
  grid: {
    rows: 4,
    cols: 4,
    rowsExpandable: true,
    colsExpandable: true,
    minRows: 2,
    minCols: 2,
  },
  rows: [{ index: 0, isHeader: true, height: 40 }],
  cols: [
    { index: 0, isHeader: true, label: 'Tasks' },
    { index: 1, isHeader: true, label: 'R' },
    { index: 2, isHeader: true, label: 'A' },
    { index: 3, isHeader: true, label: 'C/I' },
  ],
  dimensions: {
    defaultRowHeight: 120,
    defaultColWidth: 140,
    minCellHeight: 80,
    minCellWidth: 100,
  },
  contentRules: {
    allowedContent: ['text', 'sticky'],
    defaultContent: 'text',
  },
};

export const PROS_CONS_SCHEMA: TableSchema = {
  id: 'pros-cons',
  name: 'Pros and Cons',
  category: 'analysis',
  grid: {
    rows: 5,
    cols: 2,
    rowsExpandable: true,
    colsExpandable: false,
    minRows: 1,
    minCols: 2,
    maxCols: 2,
  },
  rows: [{ index: 0, isHeader: true, height: 40 }],
  cols: [
    { index: 0, isHeader: true, label: 'Pros' },
    { index: 1, isHeader: true, label: 'Cons' },
  ],
  dimensions: {
    defaultRowHeight: 80,
    defaultColWidth: 220,
    minCellHeight: 60,
    minCellWidth: 140,
  },
  contentRules: {
    allowedContent: ['text', 'sticky'],
    defaultContent: 'sticky',
  },
};

export const COMPARISON_TABLE_SCHEMA: TableSchema = {
  id: 'comparison',
  name: 'Comparison Table',
  category: 'analysis',
  grid: {
    rows: 5,
    cols: 4,
    rowsExpandable: true,
    colsExpandable: true,
    minRows: 1,
    minCols: 2,
  },
  rows: [{ index: 0, isHeader: true, height: 40 }],
  cols: [
    { index: 0, isHeader: true, label: 'Criteria' },
    { index: 1, isHeader: true, label: 'Option 1' },
    { index: 2, isHeader: true, label: 'Option 2' },
    { index: 3, isHeader: true, label: 'Option 3' },
  ],
  dimensions: {
    defaultRowHeight: 80,
    defaultColWidth: 160,
    minCellHeight: 60,
    minCellWidth: 120,
  },
  contentRules: {
    allowedContent: ['text', 'sticky'],
    defaultContent: 'text',
  },
};

export const SWIMLANE_HORIZONTAL_SCHEMA: TableSchema = {
  id: 'swimlane-horizontal',
  name: 'Swimlane (Horizontal)',
  category: 'agile',
  grid: {
    rows: 3,
    cols: 1,
    rowsExpandable: true,
    colsExpandable: true,
    minRows: 2,
    minCols: 2,
  },
  dimensions: {
    defaultRowHeight: 200,
    defaultColWidth: 440,
    minCellHeight: 100,
    minCellWidth: 40,
  },
  cols: [{ index: 0, isHeader: true, width: 40 }],
  cells: [
    { row: 0, col: 0, text: 'Lane 1', textRotation: -90 },
    { row: 1, col: 0, text: 'Lane 2', textRotation: -90 },
    { row: 2, col: 0, text: 'Lane 3', textRotation: -90 },
  ],
  contentRules: {
    allowedContent: ['shape', 'card', 'sticky'],
    defaultContent: 'shape',
  },
};

export const SWIMLANE_VERTICAL_SCHEMA: TableSchema = {
  id: 'swimlane-vertical',
  name: 'Swimlane (Vertical)',
  category: 'agile',
  grid: {
    rows: 1,
    cols: 3,
    rowsExpandable: true,
    colsExpandable: true,
    minRows: 2,
    minCols: 2,
  },
  dimensions: {
    defaultRowHeight: 340,
    defaultColWidth: 220,
    minCellHeight: 40,
    minCellWidth: 100,
  },
  rows: [{ index: 0, isHeader: true, height: 40 }],
  cells: [
    { row: 0, col: 0, text: 'Lane 1' },
    { row: 0, col: 1, text: 'Lane 2' },
    { row: 0, col: 2, text: 'Lane 3' },
  ],
  contentRules: {
    allowedContent: ['shape', 'card', 'sticky'],
    defaultContent: 'shape',
  },
};

export const FIVE_WHYS_SCHEMA: TableSchema = {
  id: 'five-whys',
  name: '5 Whys Analysis',
  category: 'analysis',
  grid: {
    rows: 5,
    cols: 2,
    rowsExpandable: true,
    colsExpandable: false,
    minRows: 5,
    minCols: 2,
  },
  dimensions: {
    defaultRowHeight: 80,
    defaultColWidth: 220,
    minCellHeight: 60,
    minCellWidth: 140,
  },
  cells: [
    { row: 0, col: 0, text: 'Why 1?' },
    { row: 1, col: 0, text: 'Why 2?' },
    { row: 2, col: 0, text: 'Why 3?' },
    { row: 3, col: 0, text: 'Why 4?' },
    { row: 4, col: 0, text: 'Why 5?' },
  ],
  contentRules: {
    allowedContent: ['text', 'sticky'],
    defaultContent: 'text',
  },
};

export const DECISION_MATRIX_SCHEMA: TableSchema = {
  id: 'decision-matrix',
  name: 'Decision Matrix',
  category: 'analysis',
  grid: {
    rows: 5,
    cols: 5,
    rowsExpandable: true,
    colsExpandable: true,
    minRows: 2,
    minCols: 3,
  },
  rows: [{ index: 0, isHeader: true, height: 40 }],
  cols: [
    { index: 0, isHeader: true, label: 'Criteria' },
    { index: 1, isHeader: true, label: 'Weight' },
    { index: 2, isHeader: true, label: 'Option 1' },
    { index: 3, isHeader: true, label: 'Option 2' },
    { index: 4, isHeader: true, label: 'Option 3' },
  ],
  dimensions: {
    defaultRowHeight: 80,
    defaultColWidth: 140,
    minCellHeight: 60,
    minCellWidth: 100,
  },
  contentRules: {
    allowedContent: ['text', 'sticky'],
    defaultContent: 'text',
  },
};

const SCHEMAS: Record<string, TableSchema> = {
  [BLANK_TABLE_SCHEMA.id]: BLANK_TABLE_SCHEMA,
  [SWOT_TABLE_SCHEMA.id]: SWOT_TABLE_SCHEMA,
  [BUSINESS_MODEL_CANVAS_SCHEMA.id]: BUSINESS_MODEL_CANVAS_SCHEMA,
  [LEAN_CANVAS_SCHEMA.id]: LEAN_CANVAS_SCHEMA,
  [KANBAN_TABLE_SCHEMA.id]: KANBAN_TABLE_SCHEMA,
  [EISENHOWER_MATRIX_SCHEMA.id]: EISENHOWER_MATRIX_SCHEMA,
  [RACI_MATRIX_SCHEMA.id]: RACI_MATRIX_SCHEMA,
  [PROS_CONS_SCHEMA.id]: PROS_CONS_SCHEMA,
  [COMPARISON_TABLE_SCHEMA.id]: COMPARISON_TABLE_SCHEMA,
  [SWIMLANE_HORIZONTAL_SCHEMA.id]: SWIMLANE_HORIZONTAL_SCHEMA,
  [SWIMLANE_VERTICAL_SCHEMA.id]: SWIMLANE_VERTICAL_SCHEMA,
  [FIVE_WHYS_SCHEMA.id]: FIVE_WHYS_SCHEMA,
  [DECISION_MATRIX_SCHEMA.id]: DECISION_MATRIX_SCHEMA,
};

const getAllSchemasWithCustom = (): Record<string, TableSchema> => {
  const custom = loadCustomSchemas();
  const merged: Record<string, TableSchema> = { ...SCHEMAS };
  for (const customSchema of custom) {
    merged[customSchema.id] = customSchema;
  }
  return merged;
};

export const getTableSchema = (schemaId: string): TableSchema | null => {
  const allSchemas = getAllSchemasWithCustom();
  return allSchemas[schemaId] ?? null;
};

export const listTableSchemas = (): TableSchema[] => {
  return Object.values(getAllSchemasWithCustom());
};

export const TABLE_SCHEMAS: Record<string, TableSchema> = SCHEMAS;

export const getAllTableSchemas = (): TableSchema[] => {
  return Object.values(getAllSchemasWithCustom());
};

export const getTableSchemasByCategory = (category: TableSchemaCategory): TableSchema[] => {
  return Object.values(getAllSchemasWithCustom()).filter((schema) => schema.category === category);
};

export const getTableSchemaCategories = (): TableSchemaCategory[] => {
  const categories = new Set<TableSchemaCategory>();
  for (const schema of Object.values(getAllSchemasWithCustom())) {
    categories.add(schema.category);
  }
  return Array.from(categories);
};

export const isCustomSchema = (schemaId: string): boolean => {
  return !(schemaId in SCHEMAS);
};
